import type { SpecContent, WorkOrder } from "@szyos/contracts";
import { assignChecks } from "@szyos/domain";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState, type FormEvent } from "react";
import { api, errorMessage } from "../api";
import { ErrorNote, useToast } from "./feedback";
import { Icon } from "../icons";

type Part = 1 | 2;

/** Zwei Knöpfe „1“ und „2“: zu welchem Auftrag ein Punkt gehört. */
function PartChoice({
  legend,
  value,
  onChange,
  disabled,
}: {
  legend: string;
  value: Part;
  onChange: (p: Part) => void;
  disabled?: boolean;
}) {
  const name = useId();
  return (
    <fieldset className="seg-group">
      <legend className="sr-only">{legend}</legend>
      {([1, 2] as const).map((p) => (
        <label key={p} className="seg">
          <input
            type="radio"
            name={name}
            checked={value === p}
            onChange={() => onChange(p)}
            disabled={disabled}
            aria-label={`Auftrag ${p}`}
          />
          <span aria-hidden="true">{p}</span>
        </label>
      ))}
    </fieldset>
  );
}

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Auftrag teilen (UX-10): Jede Funktion bekommt „1“ (bleibt in diesem Auftrag) oder „2“ (neuer Auftrag).
 * Die Prüfpunkte ordnet szyOS selbst zu (`assignChecks`); knappe Fälle zeigt es zum Nachsehen an.
 */
export function SplitOrder({ wo, spec }: { wo: WorkOrder; spec: SpecContent }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const ids = { head: useId(), title: useId() };
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(`${wo.title} (Teil 2)`.slice(0, 200));
  // Vorschlag: die vordere Hälfte bleibt, die hintere wandert in Auftrag 2.
  const firstHalf = Math.ceil(spec.mustHaves.length / 2);
  const [parts, setParts] = useState<Part[]>(() => spec.mustHaves.map((_, i) => (i < firstHalf ? 1 : 2)));
  const [checkOverride, setCheckOverride] = useState<Record<number, Part>>({});
  const [error, setError] = useState<string | null>(null);

  const moved = parts.flatMap((p, i) => (p === 2 ? [i] : []));
  const auto = assignChecks(spec, moved);
  const checks = auto.map((a, j) => ({
    part: checkOverride[j] ?? a.order,
    sure: a.sure || j in checkOverride,
  }));
  const unsure = checks.filter((c) => !c.sure).length;
  const mustIn = (p: Part) => parts.filter((x) => x === p).length;
  const checksIn = (p: Part) => checks.filter((c) => c.part === p).length;

  const split = useMutation({
    mutationFn: () =>
      api.splitWorkOrder(wo.id, {
        title,
        mustHaves: moved,
        acceptance: checks.flatMap((c, j) => (c.part === 2 ? [j] : [])),
        expectedVersion: wo.version,
      }),
    // Der Editor lädt danach die neue Revision; die Meldung nennt den neuen Auftrag, die Projektseite listet ihn.
    onSuccess: async (res) => {
      setOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["work-order", wo.id] }),
        queryClient.invalidateQueries({ queryKey: ["work-orders"] }),
      ]);
      toast(`Geteilt. „${res.created.title}“ wartet auf Freigabe.`);
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return setError("Titel für Auftrag 2 angeben.");
    if (mustIn(2) === 0) return setError("Mindestens eine Funktion auf „2“ stellen.");
    if (mustIn(1) === 0) return setError("Mindestens eine Funktion muss auf „1“ bleiben.");
    setError(null);
    split.mutate();
  };

  if (spec.mustHaves.length < 2) return null;
  if (!open) {
    return (
      <button type="button" className="btn" onClick={() => setOpen(true)}>
        <span className="btn-label">
          <Icon name="network" /> Auftrag teilen
        </span>
      </button>
    );
  }
  return (
    <form className="split-form" onSubmit={submit} noValidate aria-labelledby={ids.head}>
      <h3 id={ids.head}>Auftrag teilen</h3>
      <p className="muted">
        Tippe bei jeder Funktion auf <strong>1</strong> oder <strong>2</strong>. Vorschlag: die hintere Hälfte
        kommt in Auftrag 2.
      </p>
      <div className="split-orders">
        <div className="split-card">
          <span className="split-badge">1</span>
          <span>
            <strong>{wo.title}</strong>
            <small className="muted">dieser Auftrag, bleibt</small>
          </span>
        </div>
        <div className="split-card">
          <span className="split-badge is-new">2</span>
          <span className="split-title">
            <label htmlFor={ids.title}>Neuer Auftrag</label>
            <input id={ids.title} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
          </span>
        </div>
      </div>
      <ul className="split-rows" aria-label="Funktionen">
        {spec.mustHaves.map((m, i) => (
          <li key={`m${i}`} className={parts[i] === 2 ? "is-two" : undefined}>
            <span className="split-text">{m}</span>
            <PartChoice
              legend={`Auftrag für: ${m}`}
              value={parts[i] ?? 1}
              onChange={(p) => setParts(parts.map((x, k) => (k === i ? p : x)))}
              disabled={split.isPending}
            />
          </li>
        ))}
      </ul>
      <p className="split-summary" role="status">
        <span>
          <strong>Auftrag 1:</strong> {count(mustIn(1), "Funktion", "Funktionen")},{" "}
          {count(checksIn(1), "Prüfpunkt", "Prüfpunkte")}
        </span>
        <span>
          <strong>Auftrag 2:</strong> {count(mustIn(2), "Funktion", "Funktionen")},{" "}
          {count(checksIn(2), "Prüfpunkt", "Prüfpunkte")}
        </span>
      </p>
      <details className="split-checks" open={unsure > 0}>
        <summary>
          Prüfpunkte: von szyOS zugeordnet
          {unsure > 0 ? ` · ${count(unsure, "Punkt", "Punkte")} bitte kurz ansehen` : ", alles eindeutig"}
        </summary>
        <p className="muted">
          Prüfpunkte (Akzeptanzkriterien) sagen, woran ein Auftrag als fertig gilt. szyOS ordnet sie den
          Funktionen zu.
          {checksIn(2) === 0 && " Für Auftrag 2 erzeugt szyOS Prüfpunkte aus seinen Funktionen."}
          {checksIn(1) === 0 && " Für Auftrag 1 erzeugt szyOS Prüfpunkte aus seinen Funktionen."}
        </p>
        <ul className="split-rows">
          {spec.acceptance.map((a, j) => (
            <li key={`a${j}`} className={checks[j]?.part === 2 ? "is-two" : undefined}>
              <span className="split-text">
                {a}
                {checks[j] && !checks[j].sure && <span className="chip split-unsure">bitte prüfen</span>}
              </span>
              <PartChoice
                legend={`Auftrag für Prüfpunkt: ${a}`}
                value={checks[j]?.part ?? 1}
                onChange={(p) => setCheckOverride({ ...checkOverride, [j]: p })}
                disabled={split.isPending}
              />
            </li>
          ))}
        </ul>
      </details>
      <ErrorNote error={error} />
      <div className="idea-actions">
        <button type="submit" className="btn btn--primary" disabled={split.isPending}>
          <span className="btn-label">In zwei Aufträge teilen</span>
        </button>
        <button type="button" className="btn" onClick={() => setOpen(false)} disabled={split.isPending}>
          <span className="btn-label">Abbrechen</span>
        </button>
      </div>
    </form>
  );
}
